import { Button } from "@mui/material";
import { useRef, useState } from "react";
import { createOrgBlob, OrgBlobType } from "../../models/OrgBlob";
import { getCreds } from "../../contexts/PageContext";
import {
  deleteBlob,
  deleteModel,
  downloadBlob,
  saveModel,
  uploadBlob,
} from "../../api/connection";

export function UploadBlob({
  blobId,
  blobType,
  onUploaded,
}: {
  blobId: string;
  blobType?: OrgBlobType;
  onUploaded: (blobId: string, blob: Blob) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [blobIdState, setBlobIdState] = useState(blobId || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    const isGlb = file.name.toLowerCase().endsWith(".glb");
    const isDae = file.name.toLowerCase().endsWith(".dae");
    let mimeType = "application/octet-stream";
    if (file.name.toLowerCase().endsWith(".glb")) {
      mimeType = "model/gltf-binary";
    }
    const blob = new Blob([file], { type: mimeType });
    const arrayBuffer = await blob.arrayBuffer();
    let type = blobType;
    if (!type) {
      type = isGlb ? OrgBlobType.GLB : OrgBlobType.Unknown;
      if (isDae) {
        type = OrgBlobType.DAE;
      }
    }
    const orgBlob = await uploadOrgBlob(type, arrayBuffer);

    setBlobIdState(orgBlob._id);
    setUploading(false);
    onUploaded(orgBlob._id, blob);
  };

  const triggerFileInput = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileChange}
      />
      <Button color="secondary" onClick={triggerFileInput} disabled={uploading}>
        {/* {uploading ? 'Uploading...' : `${blobIdState || 'Upload'} ${blobType}`} */}
        {uploading ? "Uploading..." : `${blobIdState || "Upload"}`}
      </Button>
    </div>
  );
}

export async function uploadOrgBlob(
  blobType: OrgBlobType,
  arrayBuffer: ArrayBuffer,
  containerName = "techmage-assets",
  orgId = ""
) {
  const { org } = getCreds();
  const orgBlob = createOrgBlob();
  orgBlob.orgId = orgId || org._id;
  orgBlob.type = blobType;
  orgBlob.size = arrayBuffer.byteLength;
  await saveModel(orgBlob);
  await uploadBlob(containerName, `${orgBlob._id}.bin`, arrayBuffer);
  return orgBlob;
}

export async function deleteOrgBlob(
  blobId: string,
  containerName = "techmage-assets"
) {
  await deleteModel(createOrgBlob(blobId));
  // Assuming there's a function to delete the blob from storage
  await deleteBlob(containerName, `${blobId}.bin`);
}

export async function downloadOrgBlob(
  blobId: string,
  containerName = "techmage-assets"
): Promise<ArrayBuffer> {
  const res = await downloadBlob(
    containerName,
    `${blobId}.bin`,
    1000 * 60 * 60 * 24
  ); // Cache for 1 hour
  return res;
}
